import React, { FC, useCallback, useMemo, useRef, useState } from 'react';
import { DataGrid, GridToolbar } from '@material-ui/data-grid';
import i18n from '../../../i18n';
import { useSelector } from 'react-redux';
import { Box, Grid, SvgIcon, TextField, useMediaQuery } from '@material-ui/core';
import { Text, TextTypography } from '../../components/Text/Text';
import { reportItemSelector, storesReportSelector } from '../../state/adminReport/selectors';
import { makeGetReportAction, makeGetReportItemDetailsAction } from '../../state/adminReport/actions';
import { useAction, useActionDispatchOnMount } from '../../core/store/store';
import {
    MetricsDto,
    StoreCurrentState,
    StoreDto,
    YandexCheckoutApplicationDto,
    YandexCheckoutApplicationStatus,
} from '../../types';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { Icon } from '../../components/Icon/Icon';
import { DialogNew } from '../../components/Dialog/DialogNew';
import { HorizontalDivider } from '../../components/HorizontalDivider/HorizontalDivider';
import { LargeButton, LargeButtonColor } from '../../components/LargeButton/LargeButton';
import { SelectEnumControlField } from '../../components/SelectEnumControl/SelectEnumControl';
import { TranslatableEnum } from 'components/T/GetTranslationForEnum';
import { Field, reduxForm } from 'redux-form';
import { Form } from '../../forms/types';
import { ReactComponent as MoreIcon } from '../../assets/icons/main-16/elipsis-h--16.svg';
import { makeAuthLoginAsDifferentUserAction } from '../../state/auth/actions';
import { makeStoreChangeModeratedStatusRequestAction, makeStoreDeleteRequestAction } from '../../state/store/actions';
import { makeUrl } from '../../core/utils/utils';
import { Endpoint } from '../../Endpoint';
import { getRequest, postRequest } from '../../core/axios/axios';
import { StoreCardErrorDialog } from '../Store/components/StoreCardErrorDialog';
import { ExtendTrialPeriodDialog } from '../Store/components/ExtendTrialPeriodDialog';
import { CreateLinkDialog } from '../Store/components/CreateLinkDialog';
import { StoreContextMenu } from '../../components/StoreContextMenu/StoreContextMenu';
import { ReactComponent as DownLoadIcon } from '../../assets/icons/main-16/download--16.svg';
import IconButton from '@material-ui/core/IconButton';
import { StickerDownloadButton } from '../MarketingMaterials/components/StickerDownloadButton';

const useStyles = makeStyles(theme => ({
    root: {
        position: 'fixed',
    },
    details: {
        height: '84vh',
        padding: '3px',
    },
    footer: {
        borderTop: '1px solid rgba(15, 24, 37, 0.05)',
    },
    center: {
        verticalAlign: 'middle',
    },
    moreIcon: {
        width: 32,
        height: 28,
        padding: 8,
        background: '#FF7200',
        backdropFilter: 'blur(10px)',
        borderRadius: 10,
        color: theme.palette.fixedWhite.primary,
        cursor: 'pointer',
        marginTop: theme.spacing(1.5),
    },
}));

interface ReportItemDetailsProps {
    handleSubmit: (value: any) => void;
    selectedStore: ReportItemDetails & StoreDto & { subscription: string };
}

interface OperationsReportProps {
    stores: StoreEntry[];
}

export interface ReportItemDetails {
    storeId: string;
    storeName: string;
    yaCheckoutApp: YandexCheckoutApplicationDto;
    metrics: MetricsDto;
}

export interface StoreEntry {
    storeid: string;
    storename: string;
    username: string;
    customurl: string;
    creationdate: string;
    currentState: string;
    city: string;
    yaAppStatus: string;
}

export const ReportGrid: FC<OperationsReportProps> = ({ stores }) => {
    const action = useAction();
    const { t } = i18n.useTranslation();

    return (
        <DataGrid
            autoHeight
            getRowId={row => `${row.storeid}`}
            rows={stores}
            rowHeight={25}
            components={{
                Toolbar: GridToolbar,
            }}
            disableColumnMenu={true}
            columns={[
                { field: 'storeid', headerName: 'ID магазина', hide: true, width: 200 },
                { field: 'storeVisits', headerName: 'Визиты в магазин', type: 'number', width: 100 },
                {
                    field: 'storename',
                    headerName: 'Название магазина',
                    width: 200,
                    renderCell(params) {
                        return (
                            <a target="_blank" rel="noreferrer" href={'/' + params.row.customurl}>
                                {params.row.storename}
                            </a>
                        );
                    },
                },
                { field: 'country', headerName: 'Страна', width: 150 },
                { field: 'city', headerName: 'Регион', width: 150 },
                { field: 'street', headerName: 'Адрес', width: 200, hide: true },
                {
                    field: 'currentState',
                    headerName: 'Статус магазина',
                    width: 150,
                    valueFormatter: params => {
                        if (params.value === 'CARD') return 'Создается';
                        if (params.value === 'SIMPLE') return 'Витрина';
                        if (params.value === 'SELLING') return 'Продающая витрина';
                        if (params.value === 'FULL') return 'Магазин';
                        return params.value;
                    },
                },
                { field: 'username', headerName: 'Телефон регистрации', width: 200 },
                { field: 'stickersSent', 
                    headerName: 'Стикеры отправлены', 
                    hide: true,
                    width: 150, 
                    type: 'boolean' 
                },
                { field: 'email', headerName: 'Email', width: 200 },
                {
                    field: 'subscriptionExistsNow',
                    headerName: 'Платный клиент',
                    width: 150,
                    type: 'boolean',
                },
                {
                    field: 'subscriptionTill',
                    headerName: 'Подписка до',
                    width: 150,
                    // type: 'date',
                    valueFormatter: params => {
                        if (!params.value) return;
                        const datetime = new Date(Date.parse(params.value.toString()));
                        return datetime.toLocaleDateString();
                    },
                },
                {
                    field: 'orderCreationDate',
                    headerName: 'Дата последнего заказа',
                    width: 200,
                    type: 'dateTime',
                    valueFormatter: params => {
                        const datetime = params.value ? new Date(Date.parse(params.value.toString())) : null;
                        return datetime?.toLocaleString();
                    },
                },
                {
                    field: 'creationdate',
                    headerName: 'Дата создания',
                    width: 150,
                    type: 'date',
                    valueFormatter: params => {
                        const datetime = new Date(Date.parse(params.value.toString()));
                        return datetime.toLocaleDateString();
                    },
                },
                {
                    field: 'yaAppStatus',
                    headerName: 'ЮMoney заявка',
                    hide: true,
                    width: 150,
                    valueFormatter: params => {
                        if (!params.value) return;
                        return t('common:enums.ADMIN_YA_APPLICATION_STATUS.' + params.value);
                    },
                },
                { field: 'totalAmount', headerName: 'Сумма,руб', width: 150, hide: true },
                { field: 'utmSource', headerName: 'UTM', width: 150, hide: true },
                { field: 'deliveryServiceProvider', 
                    headerName: 'ApiShip', 
                    hide: true,
                    width: 150 
                },
                { field: 'vendorName', headerName: 'Имя продавца', width: 200 },
            ]}
            pageSize={100}
            onRowDoubleClick={(param, event) => {
                const selectedRowData = param.row as StoreEntry;
                action(makeGetReportItemDetailsAction, selectedRowData);
            }}
        />
    );
};

interface RowWrapper {
    row: {
        label: string;
        value: string;
        editable?: boolean | false;
        options?: any[];
        enumType?: any;
        helperText?: string;
        fieldName?: string;
        labelGridXs?: number;
        valueGridXs?: number;
    };
}

export const ReportItemDetailsRow: FC<RowWrapper> = ({ row }) => {
    const { t } = i18n.useTranslation();

    return (
        <Grid item container direction="row" spacing={1}>
            <Grid item xs={row.labelGridXs ? row.labelGridXs : 3}>
                <Text typography={TextTypography.PARA_STRONG}>{t(row.label)}:</Text>
            </Grid>
            <Grid item xs={row.valueGridXs ? row.valueGridXs : 9}>
                {row.editable ? (
                    row.options ? (
                        <Field
                            name={row.fieldName}
                            input={{ value: row.value }}
                            component={SelectEnumControlField}
                            enum={row.enumType}
                            items={row.options}
                        />
                    ) : (
                        <TextField helperText={row.helperText} />
                    )
                ) : (
                    <Text typography={TextTypography.BODY}>{row.value}</Text>
                )}
            </Grid>
        </Grid>
    );
};

const ReportItemDetailsComponent: any = reduxForm({
    form: Form.REPORT_ITEM_DETAILS_FORM,
})((props: ReportItemDetailsProps) => {
    const { selectedStore } = props;
    const styles = useStyles();
    const isLargeScreen = useMediaQuery(useTheme().breakpoints.up(1200));

    const { t } = i18n.useTranslation();

    const action = useAction();

    const menuAnchor = useRef<HTMLDivElement>(null);

    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const handleMenuClose = useCallback(() => setIsMenuOpen(false), [setIsMenuOpen]);
    const handleMenuOpen = useCallback(() => setIsMenuOpen(true), [setIsMenuOpen]);

    const isStoreModerated = selectedStore.moderated;
    const storeSubscription = selectedStore.subscription;
    const isStoreOwnerFake = selectedStore.fakeOwner;
    const isSimpleStore = selectedStore.currentState === StoreCurrentState.SIMPLE;

    const handleEdit = useCallback(() => {
        action(makeAuthLoginAsDifferentUserAction, selectedStore.vendorId);
    }, [action, selectedStore]);

    const handleDelete = useCallback(() => {
        const message = t('feed:store.deleteStoreConfirmation');
        const result = window.confirm(message);
        if (result) {
            action(makeStoreDeleteRequestAction, selectedStore.storeId);
        }
    }, [action, selectedStore]);

    const handleChangeStoreModeratedStatus = useCallback(() => {
        handleMenuClose();
        action(makeStoreChangeModeratedStatusRequestAction, selectedStore.storeId, !selectedStore.moderated);
        action(makeGetReportItemDetailsAction, { ...selectedStore, storeid: selectedStore.storeId });
    }, [action, handleMenuClose, selectedStore]);

    const [openTrialPeriodDialog, setOpenTrialPeriodDialog] = useState(false);
    const [openCreateLinkDialog, setOpenCreateLinkDialog] = useState(false);

    const handleExtendTrialPeriod = useCallback(() => setOpenTrialPeriodDialog(true), [setOpenTrialPeriodDialog]);

    const handleCreateLink = useCallback(() => setOpenCreateLinkDialog(true), [setOpenCreateLinkDialog]);

    const [errorDialogContent, setErrorDialogContent] = useState(null);

    const handleErrorDialogClose = useCallback(() => {
        setErrorDialogContent(null);
    }, [setErrorDialogContent]);

    const conventToSellingStoreError = useMemo(
        () => (
            <>
                <Text typography={TextTypography.PARA}>{t('feed:store.convertToSelling.convertError')}</Text>
                <div>
                    <LargeButton color={LargeButtonColor.LIGHT} onClick={handleErrorDialogClose}>
                        OK
                    </LargeButton>
                </div>
            </>
        ),
        [t, handleErrorDialogClose],
    );

    const handleConvertToStoreStatus = useCallback(() => {
        const url = makeUrl(
            Endpoint.STORE_SWITCH_STORE_STATUS,
            { storeId: selectedStore.storeId },
            { value: isSimpleStore ? StoreCurrentState.SELLING : StoreCurrentState.SIMPLE },
        );
        postRequest(url, {})
            .then(() => {
                window.location.reload();
            })
            .catch(() => {
                setErrorDialogContent(conventToSellingStoreError);
            });
    }, [selectedStore, setErrorDialogContent, conventToSellingStoreError]);

    const handleSetStickersSent = useCallback(() => {
        const url = makeUrl(Endpoint.STORE_SET_STICKERS_SENT, { storeId: selectedStore.storeId });
        postRequest(url, {}).then(() => {
            window.location.reload();
        });
    }, [selectedStore]);

    const handleDownloadMetricsReport = useCallback(() => {
        const url = makeUrl(Endpoint.STORE_METRICS_REPORT, { storeId: selectedStore.storeId });
        getRequest(url).then(response => {
            const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/octet-stream' }));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', response.headers['content-disposition'].split('filename=')[1].split(';')[0]);
            document.body.appendChild(link);
            link.click();
        });
    }, [selectedStore]);

    const handleExtendTrialPeriodDialogClose = useCallback(() => setOpenTrialPeriodDialog(false), [
        setOpenTrialPeriodDialog,
    ]);

    const handleCreateLinkDialogClose = useCallback(() => setOpenCreateLinkDialog(false), [setOpenCreateLinkDialog]);

    return (
        <form onSubmit={() => alert('success!')}>
            <Box className={styles.details}>
                <Box>
                    <Text typography={TextTypography.TITLE}>{selectedStore.name}</Text>
                </Box>

                {errorDialogContent && (
                    <StoreCardErrorDialog onClose={handleErrorDialogClose}>{errorDialogContent}</StoreCardErrorDialog>
                )}

                {openTrialPeriodDialog && (
                    <ExtendTrialPeriodDialog store={selectedStore} onClose={handleExtendTrialPeriodDialogClose} />
                )}

                {openCreateLinkDialog && (
                    <CreateLinkDialog store={selectedStore} onClose={handleCreateLinkDialogClose} />
                )}

                <>
                    <div ref={menuAnchor} className={styles.moreIcon} onClick={handleMenuOpen}>
                        <Icon component={MoreIcon} size={16} svgSize={16} />
                    </div>

                    <StoreContextMenu
                        anchor={menuAnchor.current}
                        isOpen={isMenuOpen}
                        isSimpleStore={isSimpleStore}
                        isStoreModerated={isStoreModerated}
                        isStoreOwnerFake={isStoreOwnerFake}
                        hasSubscription={!!storeSubscription}
                        onClose={handleMenuClose}
                        onLogInAsAdmin={handleEdit}
                        onDelete={handleDelete}
                        onChangeStoreModeratedStatus={handleChangeStoreModeratedStatus}
                        onCreateLink={handleCreateLink}
                        onExtendTrialPeriod={handleExtendTrialPeriod}
                        onConvertToSellingStore={handleConvertToStoreStatus}
                        onStickersSent={handleSetStickersSent}
                        onClone={() => {}}
                        onChangeRate={() => {}}
                    />
                </>

                {selectedStore.yaCheckoutApp && (
                    <Grid item container direction="row" spacing={1}>
                        <Grid item xs={12}>
                            <HorizontalDivider />
                        </Grid>
                        <Grid item xs={12}>
                            <Text typography={TextTypography.LEAD_LIGHT}>Заявка на подключение ЮMoney</Text>
                        </Grid>
                        <ReportItemDetailsRow
                            row={{
                                label: 'onboarding:settings.yandexCheckoutSetupCard.email',
                                value: selectedStore.yaCheckoutApp.email,
                            }}
                        />
                        <ReportItemDetailsRow
                            row={{
                                label: 'onboarding:settings.yandexCheckoutSetupCard.inn',
                                value: selectedStore.yaCheckoutApp.inn,
                            }}
                        />
                        <ReportItemDetailsRow
                            row={{
                                label: 'onboarding:settings.yandexCheckoutSetupCard.request.status',
                                value: selectedStore.yaCheckoutApp.status,
                                editable: true,
                                options: [
                                    YandexCheckoutApplicationStatus.NEW,
                                    YandexCheckoutApplicationStatus.REGISTERED,
                                    YandexCheckoutApplicationStatus.PROCESSED,
                                ],
                                fieldName: 'yaAppStatus',
                                enumType: TranslatableEnum.YA_APPLICATION_STATUS,
                            }}
                        />
                    </Grid>
                )}
                <Grid item container direction="row" spacing={1}>
                    <Grid item xs={12}>
                        <HorizontalDivider />
                    </Grid>
                    <Grid item xs={8}>
                        <Text className={styles.center} typography={TextTypography.LEAD_LIGHT}>
                            Метрики посещения за 30 дней{' '}
                        </Text>
                    </Grid>
                    <Grid item xs={4}>
                        <IconButton onClick={handleDownloadMetricsReport}>
                            <SvgIcon component={DownLoadIcon} viewBox="0 0 16 16" />
                        </IconButton>
                    </Grid>
                    <ReportItemDetailsRow
                        row={{
                            label: 'admin_report:metrics.feedShow',
                            value: numberToStr(selectedStore.metrics?.feedShow),
                            labelGridXs: 8,
                            valueGridXs: 4,
                        }}
                    />
                    <ReportItemDetailsRow
                        row={{
                            label: 'admin_report:metrics.topFeedShow',
                            value: numberToStr(selectedStore.metrics?.topFeedShow),
                            labelGridXs: 8,
                            valueGridXs: 4,
                        }}
                    />
                    <ReportItemDetailsRow
                        row={{
                            label: 'admin_report:metrics.categoryFeedShow',
                            value: numberToStr(selectedStore.metrics?.categoryFeedShow),
                            labelGridXs: 8,
                            valueGridXs: 4,
                        }}
                    />
                    <ReportItemDetailsRow
                        row={{
                            label: 'admin_report:metrics.mapFeedShow',
                            value: numberToStr(selectedStore.metrics?.mapFeedShow),
                            labelGridXs: 8,
                            valueGridXs: 4,
                        }}
                    />
                    <ReportItemDetailsRow
                        row={{
                            label: 'admin_report:metrics.searchShow',
                            value: numberToStr(selectedStore.metrics?.searchShow),
                            labelGridXs: 8,
                            valueGridXs: 4,
                        }}
                    />
                    <ReportItemDetailsRow
                        row={{
                            label: 'admin_report:metrics.productSearchShow',
                            value: numberToStr(selectedStore.metrics?.productSearchShow),
                            labelGridXs: 8,
                            valueGridXs: 4,
                        }}
                    />
                    <ReportItemDetailsRow
                        row={{
                            label: 'admin_report:metrics.storeEntrance',
                            value: numberToStr(selectedStore.metrics?.storeEntrance),
                            labelGridXs: 8,
                            valueGridXs: 4,
                        }}
                    />
                    <ReportItemDetailsRow
                        row={{
                            label: 'admin_report:metrics.calls',
                            value: numberToStr(selectedStore.metrics?.calls),
                            labelGridXs: 8,
                            valueGridXs: 4,
                        }}
                    />
                </Grid>
                <Grid item container direction="row" spacing={1}>
                    <Grid item xs={12}>
                        <HorizontalDivider />
                    </Grid>
                    <Grid item xs={12}>
                        <Text className={styles.center} typography={TextTypography.LEAD_LIGHT}>
                            Cтикеры{' '}
                        </Text>
                    </Grid>
                    <Grid item xs={12}>
                        <StickerDownloadButton storeId={selectedStore.storeId} />
                    </Grid>
                </Grid>
            </Box>
        </form>
    );
});

const numberToStr = (num?: number): string => {
    return '' + (num ? num : 0);
};

export const Report = () => {
    const storeEntries = useSelector(storesReportSelector);
    const selectedStore = useSelector(reportItemSelector);
    useActionDispatchOnMount(true, makeGetReportAction);
    const action = useAction();
    const { t } = i18n.useTranslation();

    return (
        <Box m={3}>
            <Box m={2}>
                <Text typography={TextTypography.TITLE}>Консоль администратора</Text>
            </Box>
            <Grid container direction="row" spacing={1}>
                <Grid item xs>
                    <ReportGrid stores={storeEntries} />
                </Grid>
                {/*{isLargeScreen && (*/}
                {/*    <Grid item xs={4}>*/}
                {/*        {selectedStore ? (*/}
                {/*            <Box className={styles.root}>*/}
                {/*                <Grid container item direction={'column'} justify={'space-between'}>*/}
                {/*                    <ReportItemDetailsComponent selectedStore={selectedStore} />*/}
                {/*                </Grid>*/}
                {/*            </Box>*/}
                {/*        ) : (*/}
                {/*            <Text typography={TextTypography.BODY} className={styles.root}>*/}
                {/*                Для просмотра подробной информации выберите магазин из списка*/}
                {/*            </Text>*/}
                {/*        )}*/}
                {/*    </Grid>*/}
                {/*)}*/}
            </Grid>
            {selectedStore && (
                <DialogNew
                    fullHeight={true}
                    onClose={() => action(makeGetReportItemDetailsAction)}
                    // onSubmit={handleSubmit}
                    leftButton={t('common:settings.closeButton')}
                    rightButton={t('onboarding:settings:saveButton')}
                >
                    <ReportItemDetailsComponent selectedStore={selectedStore} />
                </DialogNew>
            )}
        </Box>
    );
};
